/**
 * The range, cell, column and sheet-name fields every spreadsheet op uses.
 *
 * Ops take BOUNDED ranges only: a cell (`B2`) or a rectangle (`B2:D20`). A
 * whole column (`B:B`) or whole row (`3:5`) is refused for every op, because
 * the executor, the read-back and the touched-cell cap all need concrete
 * bounds. Input is case-insensitive and `$` markers are ignored; the parsed
 * value is the canonical upper-case form with the top-left corner first, so
 * `d20:$b$2` becomes `B2:D20`.
 *
 * Messages are written for the model to self-correct and never echo the text
 * it sent (it could be cell content pasted into the wrong field).
 */
import { z } from 'zod';
import { parseA1Range, toA1 } from './a1';
/** Excel's (and Univer's) limit on a sheet name. */
export const MAX_SHEET_NAME_CHARS = 31;
/** Bounds of a cell or rectangle; null for an open or malformed range. */
export function boundedRange(text) {
    const r = parseA1Range(text);
    if (!r || r.endRow === null || r.endColumn === null)
        return null;
    return {
        startRow: r.startRow,
        endRow: r.endRow,
        startColumn: r.startColumn,
        endColumn: r.endColumn,
    };
}
/** A1 text of bounds, top-left corner first: `B2:D20`, or `B2` for one cell. */
export const canonicalA1 = (r) => {
    const start = toA1(r.startRow, r.startColumn);
    const end = toA1(r.endRow, r.endColumn);
    return start === end ? start : `${start}:${end}`;
};
/** Rows and columns in a range: `B2:D20` is 19 rows by 3 columns. */
export const rangeShape = (r) => ({
    rows: r.endRow - r.startRow + 1,
    columns: r.endColumn - r.startColumn + 1,
});
/**
 * The data rows of a sort range whose first row is a header: the same range
 * one row shorter at the top. Null when there is no data row left. Univer's
 * `FRange.sort` has no header option, so the executor sorts this range.
 */
export function dataRangeAfterHeader(range) {
    const r = boundedRange(range);
    if (!r || r.endRow === r.startRow)
        return null;
    return canonicalA1({ ...r, startRow: r.startRow + 1 });
}
/** Where a copy of `range` lands with its top-left corner at the cell `to`. */
export function copyTargetRange(range, to) {
    const source = boundedRange(range);
    const corner = boundedRange(to);
    if (!source || !corner)
        return null;
    const { rows, columns } = rangeShape(source);
    return {
        startRow: corner.startRow,
        endRow: corner.startRow + rows - 1,
        startColumn: corner.startColumn,
        endColumn: corner.startColumn + columns - 1,
    };
}
export const rangeSchema = z.string().transform((text, ctx) => {
    const parsed = parseA1Range(text);
    if (!parsed) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'range must be a cell like B2 or a rectangle like B2:D20, with no sheet prefix (the sheet goes in "sheet")',
        });
        return z.NEVER;
    }
    const bounded = boundedRange(text);
    if (!bounded) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'range must give both corners; whole columns or rows (B:B, 3:5) are not allowed, so write e.g. B1:B200',
        });
        return z.NEVER;
    }
    return canonicalA1(bounded);
});
/** One cell, e.g. a chart anchor. */
export const cellSchema = z.string().transform((text, ctx) => {
    const r = boundedRange(text);
    if (!r || r.startRow !== r.endRow || r.startColumn !== r.endColumn) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'must be a single cell like F2, not a range',
        });
        return z.NEVER;
    }
    return canonicalA1(r);
});
/** A column letter (`A` to `XFD`), case-insensitive, upper-cased. */
export const columnLetterSchema = z
    .string()
    .regex(/^[A-Za-z]{1,3}$/, 'must be a column letter like C')
    .transform((s) => s.toUpperCase())
    .refine((s) => parseA1Range(`${s}1`) !== null, 'must be a column letter from A to XFD');
/** Zero-based index of a column letter (`A` is 0, `AB` is 27); -1 if not one. */
export const columnIndex = (letters) => parseA1Range(`${letters}1`)?.startColumn ?? -1;
/**
 * C0 controls, DEL, C1 controls and the Unicode line and paragraph
 * separators: characters that break a name or pattern where it is shown.
 */
export const hasControlChar = (s) => [...s].some((ch) => {
    const c = ch.charCodeAt(0);
    return c < 0x20 || (c >= 0x7f && c <= 0x9f) || c === 0x2028 || c === 0x2029;
});
/**
 * A sheet (tab) name, by Univer's own rule (sheets-ui `sheetNameSpecCharError`:
 * at most 31 characters, no leading or trailing `'`, none of
 * `[ ] : \ ? * /`), plus no control characters and no leading or trailing
 * whitespace. The same rule names an existing sheet and a new one.
 */
export const sheetNameSchema = z
    .string()
    .min(1, 'sheet name must not be empty')
    .max(MAX_SHEET_NAME_CHARS, `sheet name must be at most ${MAX_SHEET_NAME_CHARS} characters`)
    .refine((s) => !/[[\]:*?/\\]/.test(s), 'sheet name must not contain any of [ ] : * ? / \\')
    .refine((s) => s.trim().length > 0, 'sheet name must not be only whitespace')
    .refine((s) => s.trim() === s, 'sheet name must not start or end with whitespace')
    .refine((s) => !s.startsWith("'") && !s.endsWith("'"), "sheet name must not start or end with '")
    .refine((s) => !hasControlChar(s), 'sheet name must not contain control characters');
//# sourceMappingURL=ranges.js.map